/**
 * SeriesReformatMenu — right-click menu on a sidebar series thumbnail,
 * offering every plane except the series' own native one (e.g. an axial
 * series offers "Coronal reformat" / "Sagittal reformat"; a sagittal series
 * offers "Axial reformat" / "Coronal reformat"). This is the ONLY producer of
 * a reformat request; nothing infers one from metadata (2026-07-24 review).
 *
 * When the series cannot be resliced, all offered items are disabled and the
 * reason is shown inline. That reason used to appear as a "{plane}
 * reconstruction unavailable" placeholder squatting in a pane the user never
 * asked for; here it sits where the user is acting.
 *
 * Behavior (positioning, outside-click / Esc dismissal, roving focus that skips
 * disabled items) comes from the reporter's `useContextMenu`, which is generic
 * despite its folder. Styling is Tailwind, not the reporter's
 * `.chip-context-menu` CSS — that stylesheet is only imported by
 * PrototypeReport, so those classes are unstyled here.
 */

import React from "react";
import { createPortal } from "react-dom";

import { useContextMenu } from "../reporter/useContextMenu";
import type { PlaneOrientation } from "../../viewport/types";
import { PLANE_COLORS_HEX } from "../../constants/planeColors";

const MENU_WIDTH = 220;

const ALL_PLANES: { plane: PlaneOrientation; color: string }[] = [
  { plane: "Axial", color: PLANE_COLORS_HEX.axial },
  { plane: "Coronal", color: PLANE_COLORS_HEX.coronal },
  { plane: "Sagittal", color: PLANE_COLORS_HEX.sagittal },
];

export interface SeriesReformatMenuProps {
  anchor: { x: number; y: number };
  seriesDescription: string | null;
  /** null = reformattable; otherwise why it is not (shown to the user). */
  blocker: string | null;
  /** The series' own native plane — excluded from the offered planes
   *  ("reformat this axial to axial" is not a request anyone can make).
   *  null when the plane genuinely can't be derived — offer all three
   *  rather than guessing. */
  sourcePlane: PlaneOrientation | null;
  onGenerate: (plane: PlaneOrientation) => void;
  onClose: () => void;
}

export const SeriesReformatMenu: React.FC<SeriesReformatMenuProps> = ({
  anchor,
  seriesDescription,
  blocker,
  sourcePlane,
  onGenerate,
  onClose,
}) => {
  const { ref, style } = useContextMenu(anchor, onClose, MENU_WIDTH);
  const planes = ALL_PLANES.filter(({ plane }) => plane !== sourcePlane);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={style}
      className="fixed z-50 rounded bg-bg-2/95 py-1 ring-1 ring-border shadow-lg backdrop-blur"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-fg-muted truncate">
        {seriesDescription ?? "Series"}
      </div>
      {planes.map(({ plane, color }) => (
        <button
          key={plane}
          role="menuitem"
          type="button"
          disabled={!!blocker}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-fg hover:bg-white/10 focus-visible:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          onClick={() => {
            onGenerate(plane);
            onClose();
          }}
        >
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: color }}
          />
          {plane} reformat
        </button>
      ))}
      {blocker && (
        <div className="border-t border-border px-3 py-1.5 text-[10px] leading-tight text-warn">
          Cannot reformat: {blocker}
        </div>
      )}
    </div>,
    document.body,
  );
};

export default SeriesReformatMenu;
